/**
 * Platform limits a form has to know before it submits (`GET /platform/limits`).
 *
 * Today only the per-file upload size. It lived as `MAX_MB = 8` in
 * ImageUploader while the backend enforced its own 8 MB and the admin panel
 * edited a `max_upload_size_mb` of 10 that nothing read (AUDIT.md 12, question
 * 10). Now the backend enforces the panel's number and this is how the
 * frontend learns it, so the check before sending and the copy that prints the
 * limit say what the server will do.
 *
 * Fetched once per page and shared. Until it lands — or if it fails — the
 * seeded default stands in: the server is the one that refuses, so a stale
 * number here costs a round trip, never a wrong acceptance.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

export const DEFAULT_MAX_UPLOAD_MB = 10;

let _limits = null;

export function loadLimits() {
  if (!_limits) {
    _limits = api
      .get("/platform/limits")
      .then(({ data }) => data || {})
      .catch(() => {
        // Not cached: a transient failure must not pin the default for the
        // life of the page.
        _limits = null;
        return {};
      });
  }
  return _limits;
}

/** The per-file upload limit in MB, as `routes/uploads.py` enforces it. */
export function useUploadLimitMb() {
  const [mb, setMb] = useState(DEFAULT_MAX_UPLOAD_MB);
  useEffect(() => {
    let live = true;
    loadLimits().then((data) => {
      const n = Number(data.max_upload_size_mb);
      if (live && n > 0) setMb(n);
    });
    return () => { live = false; };
  }, []);
  return mb;
}

/**
 * How many portfolio items a FixyBee may keep — one number for every plan
 * since PLANES (2026-09-30), as `POST /users/me/portfolio` enforces it. `null`
 * until it lands: the editor must not draw a counter from a guess.
 */
export function usePortfolioMaxItems() {
  const [n, setN] = useState(null);
  useEffect(() => {
    let live = true;
    loadLimits().then((data) => {
      const value = Number(data.portfolio_max_items);
      if (live && Number.isFinite(value) && value >= 0) setN(value);
    });
    return () => { live = false; };
  }, []);
  return n;
}
